import { AnalyticsService } from '../../services/DataService';
import { Activity, BusinessStats } from '../../types/analytics';
import { BookingsApi } from './bookings';
import { Booking } from '../../types/bookings';

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 2) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function bookingToActivity(booking: Booking): Activity {
  const clientName = booking.contact?.name || 'Client';
  const serviceType = String(booking.serviceType || 'Service');
  const userInitial = clientName.charAt(0).toUpperCase();
  const status = String(booking.status || '').toLowerCase();
  const startTime = booking.startTime instanceof Date ? booking.startTime : new Date(booking.startTime as string);
  const time = formatRelativeTime(isNaN(startTime.getTime()) ? new Date() : startTime);

  if (status === 'cancelled' || status === 'canceled') {
    return {
      id: `cancel-${booking.id}`,
      type: 'cancel',
      title: 'Booking Cancelled',
      detail: `${clientName} — ${serviceType}`,
      time,
      userInitial,
    };
  }

  if (status === 'completed') {
    return {
      id: `complete-${booking.id}`,
      type: booking.price && booking.price > 0 ? 'payment' : 'complete',
      title: booking.price && booking.price > 0 ? 'Payment Received' : 'Service Completed',
      detail: `${clientName} — ${serviceType}${booking.price && booking.price > 0 ? ` — $${booking.price.toFixed(2)}` : ''}`,
      time,
      userInitial,
    };
  }

  if (status === 'overdue' || status === 'due') {
    return {
      id: `overdue-${booking.id}`,
      type: 'overdue',
      title: 'Payment Overdue',
      detail: `${clientName} — ${serviceType}`,
      time,
      userInitial,
    };
  }

  if (status === 'confirmed') {
    return {
      id: `confirm-${booking.id}`,
      type: 'confirm',
      title: 'Booking Confirmed',
      detail: `${clientName} — ${serviceType}`,
      time,
      userInitial,
    };
  }

  return {
    id: `booking-${booking.id}`,
    type: 'new booking',
    title: 'New Booking',
    detail: `${clientName} — ${serviceType}`,
    time,
    userInitial,
  };
}

export class AnalyticsApi implements AnalyticsService {
  private bookingsApi = new BookingsApi();

  async getRecentActivities(): Promise<Activity[]> {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);

    const [pastResult, upcomingResult] = await Promise.allSettled([
      this.bookingsApi.getBookingsByDateRange(thirtyDaysAgo, now),
      this.bookingsApi.getUpcomingBookings(20),
    ]);

    const pastBookings = pastResult.status === 'fulfilled' ? pastResult.value : [];
    const upcomingBookings = upcomingResult.status === 'fulfilled' ? upcomingResult.value : [];

    // Merge, deduplicate by id, filter blocked slots
    const seen = new Set<string>();
    const allBookings: Booking[] = [];
    for (const b of [...upcomingBookings, ...pastBookings]) {
      if (!seen.has(b.id) && b.type !== 'blocked') {
        seen.add(b.id);
        allBookings.push(b);
      }
    }

    // Sort by startTime descending — createdAt is not reliably populated by Wix
    const toMs = (d: Date | string) => (d instanceof Date ? d : new Date(d as string)).getTime();
    allBookings.sort((a, b) => toMs(b.startTime) - toMs(a.startTime));

    return allBookings.slice(0, 20).map(bookingToActivity);
  }

  async getBusinessStats(): Promise<BusinessStats> {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    let bookingsCount = 0;
    let completionRate = 0;
    try {
      const monthBookings = await this.bookingsApi.getBookingsByDateRange(monthStart, monthEnd);
      const realBookings = monthBookings.filter(b => b.type !== 'blocked');
      bookingsCount = realBookings.filter(b => {
        const status = String(b.status || '').toLowerCase();
        return status !== 'cancelled' && status !== 'canceled' && status !== 'refunded';
      }).length;

      // Matches the desktop Bookings Analytics definition: bookings that made it
      // to confirmed/completed, as a share of all bookings scheduled this month.
      const completedOrConfirmed = realBookings.filter(b => {
        const status = String(b.status || '').toLowerCase();
        return status === 'confirmed' || status === 'completed';
      }).length;
      completionRate = realBookings.length > 0
        ? Math.round((completedOrConfirmed / realBookings.length) * 100)
        : 0;
    } catch {
      bookingsCount = 0;
      completionRate = 0;
    }

    return {
      newClients: 0,
      completionRate,
      revenue: 0,
      growth: 0,
      bookingsCount,
      customerRetentionRate: 0,
    };
  }
}
